import type { PhysicalStore } from '../../domain/models/PhysicalStore';
import type { PhysicalStoresDetails } from '../../domain/models/PhysicalStoresDetails';
import type { StoreLocatorData } from '../../domain/models/StoreLocatorData';
import type {
  PhysicalStoreDto,
  PhysicalStoresDetailsDto,
  StoreLocatorDataDto,
  StoreLocatorResponseDto,
} from '../dto/physicalStore.dto';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function mapPhysicalStoreDto(dto: PhysicalStoreDto): PhysicalStore | null {
  if (typeof dto.id !== 'number' || !dto.sid || !dto.storeName) return null;

  return {
    id: dto.id,
    sid: dto.sid,
    storeName: dto.storeName ?? '',
    address: dto.address ?? '',
    cityName: dto.cityName ?? '',
    cityGroup: dto.cityGroup ?? '',
    contactNumber: dto.contactNumber ?? '',
    postCode: dto.postCode ?? '',
    storeReviewLink: dto.storeReviewLink ?? null,
    destinationPlaceId: dto.destinationPlaceId ?? '',
    storeTimings: dto.storeTimings ?? '',
    whatsappNumber: dto.whatsappNumber ?? '',
    storeUrl: dto.storeUrl ?? '',
    latitude: typeof dto.latitude === 'number' ? dto.latitude : 0,
    longitude: typeof dto.longitude === 'number' ? dto.longitude : 0,
  };
}

function mapCityToStoresMap(rawMap: unknown): Record<string, PhysicalStore[]> {
  if (!isObject(rawMap)) return {};

  const cityToStoresMap: Record<string, PhysicalStore[]> = {};

  for (const [cityName, stores] of Object.entries(rawMap)) {
    if (!Array.isArray(stores)) continue;
    const mapped = stores
      .map((store) => mapPhysicalStoreDto(store as PhysicalStoreDto))
      .filter((store): store is PhysicalStore => store !== null);
    if (mapped.length > 0) {
      cityToStoresMap[cityName] = mapped;
    }
  }

  return cityToStoresMap;
}

export function parseStoreLocatorSeoSchema(schema: unknown): unknown | undefined {
  if (schema == null || schema === '') return undefined;
  if (typeof schema === 'string') {
    try {
      return JSON.parse(schema) as unknown;
    } catch {
      return undefined;
    }
  }
  return schema;
}

export function mapPhysicalStoresDetailsDto(dto: unknown): PhysicalStoresDetails {
  if (!isObject(dto)) return {};

  const cityToStoresMap = mapCityToStoresMap(dto.cityToStoresMap);
  return { cityToStoresMap };
}

export function mapPhysicalStoresDetailsResponse(response: unknown): PhysicalStoresDetails {
  return mapPhysicalStoresDetailsDto(response as PhysicalStoresDetailsDto);
}

export function mapStoreLocatorDto(dto: StoreLocatorDataDto | undefined): StoreLocatorData {
  if (!dto) return {};

  const cityToStoresMap = mapCityToStoresMap(dto.storeByCityGroup);
  const numberOfStores =
    typeof dto.numberOfStores === 'number' && Number.isFinite(dto.numberOfStores)
      ? dto.numberOfStores
      : undefined;

  return {
    numberOfStores,
    cityToStoresMap,
    seoSchema: parseStoreLocatorSeoSchema(dto.seoData?.schema),
    currentCityName:
      typeof dto.currentCity?.cityName === 'string' ? dto.currentCity.cityName : undefined,
    currentCityIsInList: dto.currentCityIsInList === true,
  };
}

export function mapStoreLocatorResponse(response: unknown): StoreLocatorData {
  if (!isObject(response)) return {};

  const wrapped = response as StoreLocatorResponseDto;
  if (wrapped.hasError === true) return {};

  const payload = isObject(wrapped.data) ? wrapped.data : (response as StoreLocatorDataDto);
  return mapStoreLocatorDto(payload);
}
